import React, { useState, useEffect, useRef } from 'react';
import { createBooking } from '../services/BookingService';
import { message, DatePicker, TimePicker, Select, Input, Form, App } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/vi';
import { getBranches } from '../services/BranchService';
import { getServiceTitles } from '../services/ServiceService';
import {
    Phone, User, Car, MapPin, Wrench, Calendar, Clock, Info,
    ArrowRight, ArrowLeft, Sparkles, Building2, UserCheck, ChevronRight,
    CheckCircle2, Shield, Zap
} from 'lucide-react';
import { normalizeImageUrl } from '../utils/imageUtils';
import BookingSuccessModal from '../components/BookingSuccessModal';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Ho_Chi_Minh');

const { Option } = Select;
const { TextArea } = Input;
const CAR_BRANDS = ['Toyota', 'Honda', 'Ford', 'Mazda', 'Hyundai', 'Kia', 'VinFast', 'Mercedes', 'BMW', 'Audi', 'Mitsubishi', 'Suzuki', 'Nissan', 'Khác'];
const isValidPhone = (p) => /^(\+84|84|0)[0-9]{8,10}$/.test(p);

/* Styled input wrapper */
const FieldWrap = ({ icon, label, required, children }) => (
    <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-500">
            <span className="text-red-600">{icon}</span>
            {label}
            {required && <span className="text-red-500">*</span>}
        </label>
        {children}
    </div>
);

const inputCls = "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-900 font-semibold text-sm placeholder:text-slate-300 focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all";

const BookingPage = () => {
    const { modal, message: msgApi } = App.useApp();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [data, setData] = useState({
        phone: '', name: '', carBrand: '', carYear: '', carModel: '', carName: '',
        garageId: null, branchId: null, serviceId: null, date: null, time: null, note: ''
    });
    const [garages, setGarages] = useState([]);
    const [services, setServices] = useState([]);
    const [branches, setBranches] = useState([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [lastBooking, setLastBooking] = useState(null);
    const [formError, setFormError] = useState(null);

    const isFetchingRef = useRef(false);

    useEffect(() => {
        const loadData = async () => {
            if (isFetchingRef.current) return;
            isFetchingRef.current = true;
            try {
                const [bRes, sRes] = await Promise.all([getBranches(), getServiceTitles()]);
                let bData = Array.isArray(bRes) ? bRes : (bRes?.content || bRes?.items || []);
                const branchesList = bData.map(b => ({
                    id: b._id || b.id, name: b.name,
                    address: b.address || '',
                    lat: b.lat || '', lng: b.lng || '',
                    imageUrl: b.imageUrl || '',
                    phone: b.phone || ''
                }));
                setGarages([{ id: 1, name: "Trung Anh Garage", branches: branchesList }]);
                const sList = Array.isArray(sRes) ? sRes : (sRes?.items || []);
                setServices(sList.filter(s => s.isActive !== false).map(s => ({ id: s._id || s.id, name: s.title })));
            } catch (e) { console.error(e); }
            finally { isFetchingRef.current = false; }
        };
        loadData();
    }, []);

    useEffect(() => {
        if (data.garageId) {
            const g = garages.find(g => g.id === data.garageId);
            setBranches(g ? g.branches : []);
        } else {
            setBranches([]);
            setData(p => ({ ...p, branchId: null }));
        }
    }, [data.garageId, garages]);

    const setField = (k, v) => { setData(p => ({ ...p, [k]: v })); };

    const handleNext = async () => {
        try {
            setFormError(null);
            await form.validateFields(['phone', 'name', 'carBrand', 'carYear', 'carModel', 'carName']);
            if (!isValidPhone(data.phone)) {
                setFormError("Số điện thoại không hợp lệ (VD: 0912345678)");
                return;
            }
            setStep(1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
            setFormError("Vui lòng hoàn tất TẤT CẢ thông tin bắt buộc.");
        }
    };

    const handleSubmit = async () => {
        try {
            setFormError(null);
            await form.validateFields(['garageId', 'branchId', 'serviceId', 'date', 'time']);
            setSubmitting(true);
            const garage = garages.find(g => g.id === data.garageId);
            const branch = branches.find(b => b.id === data.branchId);
            const service = services.find(s => s.id === data.serviceId);
            const payload = {
                phone: data.phone, name: data.name,
                carBrand: data.carBrand, carYear: data.carYear, carModel: data.carModel, carName: data.carName,
                garage: garage?.name || '', branch: branch?.name || '', branchAddress: branch?.address || '',
                serviceType: service?.name || '',
                scheduleAt: dayjs.tz(`${data.date} ${data.time}`, 'YYYY-MM-DD HH:mm', 'Asia/Ho_Chi_Minh').toISOString(),
                note: data.note || '', status: 'PENDING'
            };
            await createBooking(payload);

            // Set success data for modal
            setLastBooking({
                ...payload,
                dateDisplay: dayjs(data.date).format('DD/MM/YYYY'),
                timeDisplay: data.time
            });
            setShowSuccess(true);

            setStep(0);
            setData({ phone: '', name: '', carBrand: '', carYear: '', carModel: '', carName: '', garageId: null, branchId: null, serviceId: null, date: null, time: null, note: '' });
            form.resetFields();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
            if (e.response?.data?.error || typeof e.response?.data === 'string') {
                setFormError(e.response.data.error || e.response.data);
            } else if (e.errorFields) {
                setFormError("Vui lòng hoàn tất TẤT CẢ thông tin bắt buộc.");
            } else {
                setFormError("Có lỗi xảy ra khi đặt lịch. Vui lòng thử lại!");
            }
            console.error(e);
        } finally { setSubmitting(false); }
    };

    const selectCls = {
        width: '100%',
        borderRadius: 12,
        height: 48,
    };

    return (
        <main className="min-h-screen pb-20" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0f172a 100%)' }}>
            {/* ════ BREADCRUMBS ════ */}
            <div className="py-4 mt-[50px]">
                <div className="container px-4">
                    <nav className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider">
                        <a href="/" className="hover:text-red-400 transition-colors no-underline text-slate-400">Trang chủ</a>
                        <span className="opacity-30 text-white">/</span>
                        <span className="text-white font-bold">Đặt lịch hẹn</span>
                    </nav>
                </div>
            </div>

            <div className="container" style={{ maxWidth: 1000 }}>
                {/* Hero Header */}
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-600/30 rounded-full px-4 py-1.5 mb-4">
                        <Sparkles size={14} className="text-red-400" />
                        <span className="text-red-400 text-[11px] font-black uppercase tracking-widest">Đặt lịch trực tuyến</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tighter leading-tight mb-3">
                        Hẹn Lịch <span className="text-red-500">Sửa Chữa</span>
                    </h1>
                    <p className="text-slate-400 text-sm max-w-md mx-auto">Chỉ vài bước đơn giản — chúng tôi sẽ xác nhận và có mặt đúng hẹn</p>

                    {/* Features row */}
                    <div className="flex items-center justify-center gap-8 mt-6">
                        {[
                            { icon: <Zap size={14} />, text: 'Xác nhận trong 30 phút' },
                            { icon: <Shield size={14} />, text: 'Bảo hành dịch vụ' },
                            { icon: <CheckCircle2 size={14} />, text: 'Kỹ thuật viên chuyên nghiệp' },
                        ].map((f, i) => (
                            <div key={i} className="flex items-center gap-2 text-slate-400 text-xs font-semibold">
                                <span className="text-red-500">{f.icon}</span>
                                {f.text}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Step Indicator */}
                <div className="flex items-center justify-center gap-3 mb-8">
                    {[
                        { label: 'Thông tin khách', icon: <UserCheck size={16} /> },
                        { label: 'Dịch vụ & Lịch hẹn', icon: <Wrench size={16} /> },
                    ].map((s, i) => (
                        <React.Fragment key={i}>
                            <div className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-wider transition-all duration-500 ${step === i
                                ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                                : step > i
                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                    : 'bg-white/5 text-slate-500 border border-white/10'
                                }`}>
                                {step > i ? <CheckCircle2 size={15} /> : s.icon}
                                <span className="hidden sm:block">{s.label}</span>
                                <span className="sm:hidden">Bước {i + 1}</span>
                            </div>
                            {i < 1 && <ChevronRight size={16} className="text-slate-600" />}
                        </React.Fragment>
                    ))}
                </div>

                {/* Main Card */}
                <div className="bg-white rounded-[2rem] overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.4)]">
                    {/* Card Header Strip */}
                    <div className="h-1.5 bg-gradient-to-r from-slate-900 via-red-600 to-slate-900" />

                    <div className="p-8 md:p-12">
                        <Form form={form} layout="vertical" colon={false}>
                            {step === 0 ? (
                                <div>
                                    {/* Section: Customer */}
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center text-blue-600">
                                            <UserCheck size={20} />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider m-0">Thông tin chủ xe</h3>
                                            <p className="text-xs text-slate-400 m-0">Bắt buộc điền đầy đủ</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                                        <FieldWrap icon={<Phone size={14} />} label="Số điện thoại" required>
                                            <Form.Item name="phone" rules={[{ required: true, message: 'Vui lòng nhập số điện thoại' }]} className="mb-0">
                                                <input className={inputCls} placeholder="0912 345 678" value={data.phone} onChange={e => { setField('phone', e.target.value); form.setFieldValue('phone', e.target.value); }} maxLength={12} />
                                            </Form.Item>
                                        </FieldWrap>
                                        <FieldWrap icon={<User size={14} />} label="Họ và tên" required>
                                            <Form.Item name="name" rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]} className="mb-0">
                                                <input className={inputCls} placeholder="Nguyễn Văn A" value={data.name} onChange={e => { setField('name', e.target.value); form.setFieldValue('name', e.target.value); }} />
                                            </Form.Item>
                                        </FieldWrap>
                                    </div>

                                    {/* Divider */}
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="flex-1 h-px bg-slate-100" />
                                        <div className="flex items-center gap-2 bg-red-50 rounded-full px-4 py-1.5">
                                            <Car size={14} className="text-red-600" />
                                            <span className="text-[11px] font-black uppercase tracking-widest text-red-600">Thông tin phương tiện</span>
                                        </div>
                                        <div className="flex-1 h-px bg-slate-100" />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                                        <FieldWrap icon={<Car size={14} />} label="Hãng xe" required>
                                            <Form.Item name="carBrand" rules={[{ required: true, message: 'Vui lòng chọn hãng xe' }]} className="mb-0">
                                                <Select size="large" placeholder="Chọn hãng" showSearch value={data.carBrand || undefined} onChange={v => { setField('carBrand', v); form.setFieldValue('carBrand', v); }} allowClear style={selectCls}
                                                    className="custom-select">
                                                    {CAR_BRANDS.map(b => <Option key={b} value={b}>{b}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </FieldWrap>
                                        <FieldWrap icon={<Calendar size={14} />} label="Năm sản xuất" required>
                                            <Form.Item name="carYear" rules={[{ required: true, message: 'Vui lòng chọn năm' }]} className="mb-0">
                                                <Select size="large" placeholder="Năm" value={data.carYear || undefined} onChange={v => { setField('carYear', v); form.setFieldValue('carYear', v); }} allowClear style={selectCls}>
                                                    {Array.from({ length: 30 }, (_, i) => { const y = new Date().getFullYear() - i; return <Option key={y} value={String(y)}>{y}</Option>; })}
                                                </Select>
                                            </Form.Item>
                                        </FieldWrap>
                                        <FieldWrap icon={<Info size={14} />} label="Biển số" required>
                                            <Form.Item name="carModel" rules={[{ required: true, message: 'Vui lòng nhập biển số' }]} className="mb-0">
                                                <input className={inputCls} placeholder="Vios - 30H-123.45" value={data.carModel} onChange={e => { setField('carModel', e.target.value); form.setFieldValue('carModel', e.target.value); }} />
                                            </Form.Item>
                                        </FieldWrap>
                                        <FieldWrap icon={<Car size={14} />} label="Tên xe" required>
                                            <Form.Item name="carName" rules={[{ required: true, message: 'Vui lòng nhập tên xe' }]} className="mb-0">
                                                <input className={inputCls} placeholder="Ví dụ: Toyota Vios 2022, Honda City 2021..." value={data.carName} onChange={e => { setField('carName', e.target.value); form.setFieldValue('carName', e.target.value); }} />
                                            </Form.Item>
                                        </FieldWrap>
                                    </div>

                                    {/* Trust badges */}
                                    <div className="grid grid-cols-3 gap-4 p-5 bg-slate-50 rounded-2xl mb-8">
                                        {[
                                            { icon: '🔒', title: 'Bảo mật 100%', desc: 'Thông tin được mã hóa' },
                                            { icon: '⚡', title: 'Xác nhận nhanh', desc: 'Trong vòng 30 phút' },
                                            { icon: '🏆', title: 'Kỹ thuật cao', desc: 'Chứng chỉ quốc tế' },
                                        ].map((b, i) => (
                                            <div key={i} className="text-center">
                                                <div className="text-xl mb-1">{b.icon}</div>
                                                <div className="text-xs font-black text-slate-700">{b.title}</div>
                                                <div className="text-[10px] text-slate-400">{b.desc}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {formError && (
                                        <div className="flex items-center gap-2 p-3 mb-6 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-semibold">
                                            <span>⚠️</span> {formError}
                                        </div>
                                    )}
                                    <div className="flex justify-end">
                                        <button onClick={handleNext}
                                            className="flex items-center gap-3 bg-slate-900 hover:bg-red-600 text-white font-black text-xs uppercase tracking-widest px-10 py-4 rounded-2xl transition-all duration-500 shadow-xl hover:shadow-red-600/30 group">
                                            Tiếp theo
                                            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    {/* Section: Service */}
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-red-600/10 rounded-xl flex items-center justify-center text-red-600">
                                            <Wrench size={20} />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider m-0">Chọn dịch vụ & Lịch hẹn</h3>
                                            <p className="text-xs text-slate-400 m-0">Chọn cơ sở, dịch vụ và thời gian phù hợp</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                                        <FieldWrap icon={<Building2 size={14} />} label="Hệ thống Garage" required>
                                            <Form.Item name="garageId" rules={[{ required: true, message: 'Vui lòng chọn Garage' }]} className="mb-0">
                                                <Select size="large" placeholder="Chọn Garage" value={data.garageId || undefined}
                                                    onChange={v => { setField('garageId', v); form.setFieldValue('garageId', v); }} allowClear style={selectCls}>
                                                    {garages.map(g => <Option key={g.id} value={g.id}>{g.name}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </FieldWrap>
                                        <FieldWrap icon={<Wrench size={14} />} label="Dịch vụ yêu cầu" required>
                                            <Form.Item name="serviceId" rules={[{ required: true, message: 'Vui lòng chọn dịch vụ' }]} className="mb-0">
                                                <Select size="large" placeholder="Chọn dịch vụ" value={data.serviceId || undefined}
                                                    onChange={v => { setField('serviceId', v); form.setFieldValue('serviceId', v); }} allowClear style={selectCls}>
                                                    {services.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </FieldWrap>
                                    </div>

                                    {data.garageId && (
                                        <div className="mb-6">
                                            <div className="flex items-center gap-2 mb-3">
                                                <MapPin size={14} className="text-red-600" />
                                                <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">Chọn chi nhánh <span className="text-red-500">*</span></span>
                                            </div>
                                            <Form.Item name="branchId" noStyle rules={[{ required: true }]}>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {branches.map(b => {
                                                        const isSelected = data.branchId === b.id;
                                                        const addrStr = typeof b.address === 'object'
                                                            ? (b.address.street || b.address.fullAddress || '')
                                                            : (b.address || '');
                                                        return (
                                                            <div key={b.id}
                                                                onClick={() => { setField('branchId', b.id); form.setFieldValue('branchId', b.id); }}
                                                                style={{
                                                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                                                                    padding: '9px 11px',
                                                                    border: isSelected ? '2px solid #dc2626' : '1.5px solid #e2e8f0',
                                                                    borderRadius: 10,
                                                                    background: isSelected ? '#fff5f5' : '#fafafa',
                                                                    boxShadow: isSelected ? '0 0 0 3px rgba(220,38,38,0.08)' : 'none',
                                                                    transition: 'all 0.18s'
                                                                }}>
                                                                {/* Thumbnail */}
                                                                <div style={{ width: 44, height: 44, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(135deg,#0f172a,#b91c1c)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    {b.imageUrl
                                                                        ? <img src={normalizeImageUrl(b.imageUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                                                                        : <MapPin size={18} color="rgba(255,255,255,0.55)" />
                                                                    }
                                                                </div>
                                                                {/* Info */}
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <div style={{ fontWeight: 700, fontSize: 12, color: isSelected ? '#dc2626' : '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                                                                    {addrStr && <div style={{ fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>📍 {addrStr}</div>}
                                                                    {b.phone && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>📞 {b.phone}</div>}
                                                                </div>
                                                                {isSelected && (
                                                                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </Form.Item>
                                        </div>
                                    )}

                                    {/* Divider */}
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="flex-1 h-px bg-slate-100" />
                                        <div className="flex items-center gap-2 bg-red-50 rounded-full px-4 py-1.5">
                                            <Clock size={14} className="text-red-600" />
                                            <span className="text-[11px] font-black uppercase tracking-widest text-red-600">Chọn thời gian</span>
                                        </div>
                                        <div className="flex-1 h-px bg-slate-100" />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                                        <FieldWrap icon={<Calendar size={14} />} label="Ngày hẹn" required>
                                            <Form.Item name="date" rules={[{ required: true, message: 'Vui lòng chọn ngày hẹn' }]} className="mb-0">
                                                <DatePicker size="large" style={{ width: '100%', borderRadius: 12 }} placeholder="Chọn ngày"
                                                    format="DD/MM/YYYY" value={data.date ? dayjs(data.date) : null}
                                                    onChange={d => { const v = d ? d.format('YYYY-MM-DD') : null; setField('date', v); form.setFieldValue('date', d); }}
                                                    disabledDate={c => c && c < dayjs().startOf('day')} />
                                            </Form.Item>
                                        </FieldWrap>
                                        <FieldWrap icon={<Clock size={14} />} label="Giờ hẹn" required>
                                            <Form.Item name="time" rules={[{ required: true, message: 'Vui lòng chọn giờ hẹn' }]} className="mb-0">
                                                <TimePicker size="large" style={{ width: '100%', borderRadius: 12 }} placeholder="Chọn giờ"
                                                    format="HH:mm" value={data.time ? dayjs(data.time, 'HH:mm') : null}
                                                    onChange={t => { const v = t ? t.format('HH:mm') : null; setField('time', v); form.setFieldValue('time', t); }}
                                                    minuteStep={30} hideDisabledOptions
                                                    disabledTime={() => ({
                                                        disabledHours: () => [0, 1, 2, 3, 4, 5, 6, 7, 18, 19, 20, 21, 22, 23],
                                                        disabledMinutes: (h) => h === 17 ? Array.from({ length: 30 }, (_, i) => i + 31) : []
                                                    })} />
                                            </Form.Item>
                                        </FieldWrap>
                                    </div>

                                    <FieldWrap icon={<Info size={14} />} label='Mô tả tình trạng xe (Không bắt buộc)'>
                                        <Form.Item name="note" className="mb-0">
                                            <textarea className={`${inputCls} min-h-[100px] resize-none`} placeholder="Ví dụ: Kiểm tra phanh, Thay dầu, Tiếng kêu lạ ở bánh trước..."
                                                value={data.note} onChange={e => { setField('note', e.target.value); form.setFieldValue('note', e.target.value); }} />
                                        </Form.Item>
                                    </FieldWrap>

                                    {formError && (
                                        <div className="flex items-center gap-2 p-3 mt-6 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-semibold">
                                            <span>⚠️</span> {formError}
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-100">
                                        <button onClick={() => { setStep(0); setFormError(null); }}
                                            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 font-bold text-sm border border-slate-200 hover:border-slate-400 px-6 py-3 rounded-xl transition-all group">
                                            <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
                                            Quay lại
                                        </button>
                                        <button onClick={handleSubmit} disabled={submitting}
                                            className="flex items-center gap-3 text-white font-black text-xs uppercase tracking-widest px-10 py-4 rounded-2xl transition-all duration-500 shadow-xl disabled:opacity-50 group"
                                            style={{ background: submitting ? '#94a3b8' : 'linear-gradient(135deg, #0f172a 0%, #b91c1c 100%)', boxShadow: submitting ? 'none' : '0 10px 40px rgba(185,28,28,0.35)' }}>
                                            {submitting ? (
                                                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Đang xử lý...</>
                                            ) : (
                                                <>Chốt lịch hẹn <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </Form>
                    </div>
                </div>

                {/* Bottom trust strip */}
                <div className="flex items-center justify-center gap-8 mt-8 pb-4">
                    {['🔧 Thợ máy được đào tạo bài bản', '📍 15+ kinh nghiệm', '⭐ 4.9/5 đánh giá khách hàng'].map((t, i) => (
                        <span key={i} className="text-slate-400 text-xs font-medium">{t}</span>
                    ))}
                </div>
                {/* Success Modal */}
                <BookingSuccessModal
                    visible={showSuccess}
                    onClose={() => setShowSuccess(false)}
                    bookingData={lastBooking}
                />
            </div>
        </main>
    );
};

export default BookingPage;
